import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../lib/i18n.js';
import { matches } from '../lib/lists.js';

export interface PickerOption {
  readonly id: string;
  readonly label: string;
  readonly sub?: string;
  readonly right?: string;
}

/**
 * Type-to-search picker for parties and items: a text box that filters `options` as you type,
 * Enter picks the first match, and the chosen one shows as a row with a Change button.
 * With `noneLabel`, an empty value is a real choice (walk-in customer) shown with that label.
 */
export function Picker(props: {
  value: string;
  options: ReadonlyArray<PickerOption>;
  onChange: (id: string) => void;
  placeholder: string;
  noneLabel?: string;
  invalid?: boolean;
  autoFocus?: boolean;
  /** Escape with nothing chosen yet (the account chooser's search mode). */
  onCancel?: () => void;
  testId?: string;
}) {
  const chosen = props.value ? props.options.find((o) => o.id === props.value) : undefined;
  const hasChoice = !!chosen || (props.value === '' && props.noneLabel !== undefined);
  const [editing, setEditing] = useState(!hasChoice);
  const [q, setQ] = useState('');
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editing) input.current?.focus();
  }, [editing]);

  const pick = (id: string) => {
    props.onChange(id);
    setQ('');
    setEditing(false);
  };

  if (!editing && hasChoice) {
    return (
      <div class="picker picker-chosen" data-testid={props.testId}>
        <div class="row-main">
          <div class="row-title">{chosen ? chosen.label : props.noneLabel}</div>
          {chosen?.sub && <div class="row-sub">{chosen.sub}</div>}
        </div>
        {chosen?.right && <div class="row-amt-sub">{chosen.right}</div>}
        <button type="button" class="btn btn-ghost" onClick={() => setEditing(true)}>
          {t('picker_change')}
        </button>
      </div>
    );
  }

  const ql = q.trim();
  const list = (
    ql ? props.options.filter((o) => matches(`${o.label} ${o.sub ?? ''}`, ql)) : props.options
  ).slice(0, 8);
  const showNone = props.noneLabel !== undefined && (!ql || matches(props.noneLabel, ql));
  return (
    <div class="picker" data-testid={props.testId}>
      <input
        ref={input}
        class="input"
        value={q}
        placeholder={props.placeholder}
        aria-invalid={props.invalid ? 'true' : undefined}
        autoFocus={props.autoFocus}
        onInput={(e) => setQ((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (list[0]) pick(list[0].id);
            else if (showNone) pick('');
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setQ('');
            if (hasChoice) setEditing(false);
            else props.onCancel?.();
          }
        }}
      />
      <div class="pick">
        {showNone && (
          <button type="button" onClick={() => pick('')}>
            <span class="muted">{props.noneLabel}</span>
          </button>
        )}
        {list.map((o) => (
          <button type="button" key={o.id} onClick={() => pick(o.id)}>
            <span>
              {o.label}
              {o.sub && <span class="sub"> {o.sub}</span>}
            </span>
            {o.right && <span class="sub">{o.right}</span>}
          </button>
        ))}
        {list.length === 0 && !showNone && (
          <div class="help" style={{ padding: '8px 10px' }}>
            {t('picker_noMatch')}
          </div>
        )}
        {!ql && props.options.length > 8 && (
          <div class="help" style={{ padding: '6px 10px' }}>
            {t('picker_typeMore', { n: String(props.options.length - 8) })}
          </div>
        )}
      </div>
    </div>
  );
}
